import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

const UserMetadataContext = createContext();

export function UserMetadataProvider({ children }) {
  const { user, isAuthenticated, isLoading: auth0Loading, getAccessTokenSilently } = useAuth0();
  const [userMetadata, setUserMetadata] = useState(null);
  const [tenantStatus, setTenantStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMetadata = async () => {
      setIsLoading(true);
      
      if (isAuthenticated && user) {
        try {
          // ===== TESTING/DEBUG LOGGING =====
          console.log('🔐 AUTH0 USER PROFILE (for testing):', {
            email: user.email,
            name: user.name,
            nickname: user.nickname,
            sub: user.sub,
            picture: user.picture,
            email_verified: user.email_verified,
            updated_at: user.updated_at,
            full_user_object: user
          });

          // Get and log JWT token for testing
          try {
            const jwtToken = await getAccessTokenSilently();
            console.log('🎫 JWT ACCESS TOKEN (for testing):', jwtToken);

            // Decode JWT payload for easier reading (just the payload part)
            const tokenParts = jwtToken.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              console.log('🔍 JWT PAYLOAD DECODED (for testing):', payload);
            }
          } catch (tokenError) {
            console.log('⚠️ Could not retrieve JWT token:', tokenError);
          }
          // ===== END TESTING/DEBUG LOGGING =====

          // Log the full user object and all its keys
          console.log('Auth0 user authenticated:', user?.email);
          console.log('Available user keys:', Object.keys(user));

          // Try to get metadata directly from Auth0 if not present in the token
          let metadata = {};
          
          // Try different namespaces for company info in the token
          const namespaces = [
            'https://trustx.cloud/claims/',
            'https://trustx.io/',
            'https://api-dev.trustx.cloud/',
            '',
            'company',
            'https://trustx.cloud/company'
          ];

          // First check if company is available in the token
          let companyFromToken = null;
          for (const ns of namespaces) {
            const key = typeof ns === 'string' && ns.length > 0 ? `${ns}company` : 'company';
            if (user[key]) {
              console.log(`Found company in token with key: ${key} = ${user[key]}`);
              companyFromToken = user[key];
              break;
            }
          }

          if (companyFromToken) {
            console.log(`Using company from token: ${companyFromToken}`);
            metadata.company = companyFromToken;
          } else {
            console.log('No company found in token, checking Auth0 user metadata API');
            
            try {
              // Try to get metadata from Auth0 API
              const accessToken = await getAccessTokenSilently({
                authorizationParams: {
                  audience: `https://${process.env.REACT_APP_AUTH0_DOMAIN}/api/v2/`,
                  scope: 'read:current_user',
                },
              });
              
              const userDetailsByIdUrl = `https://${process.env.REACT_APP_AUTH0_DOMAIN}/api/v2/users/${user.sub}`;
              const metadataResponse = await fetch(userDetailsByIdUrl, {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              });
              
              if (metadataResponse.ok) {
                const userData = await metadataResponse.json();
                console.log('Got user metadata from Auth0:', userData);
                
                if (userData.user_metadata?.company) {
                  metadata.company = userData.user_metadata.company;
                  console.log(`Using company from user_metadata: ${metadata.company}`);
                } else if (userData.app_metadata?.company) {
                  metadata.company = userData.app_metadata.company;
                  console.log(`Using company from app_metadata: ${metadata.company}`);
                } else {
                  console.warn('No company found in Auth0 user metadata');
                }
              } else {
                console.warn('Failed to fetch user metadata from Auth0 API');
              }
            } catch (apiError) {
              console.error('Error fetching metadata from Auth0:', apiError);
            }
          }
          
          // If still no company, set error state
          // Treat 'N/A', empty string, null, or undefined as no company
          if (!metadata.company || metadata.company === 'N/A' || metadata.company.trim() === '') {
            console.error('No tenant information available. User must be assigned to a tenant.');
            setError('No tenant information available. Please contact your administrator to be assigned to a tenant.');
            setUserMetadata(null);
            setTenantStatus(null);
          } else {
            console.log('Company found in JWT:', metadata.company);

            // DO NOT set userMetadata yet - wait until tenant validation succeeds
            // Check tenant status via GraphQL first
            try {
              const accessToken = await getAccessTokenSilently();
              const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;

              const tenantStatusQuery = `
                query GetTenantStatus {
                  getTenantStatus {
                    tenantId
                    tenantName
                    status
                    createdAt
                  }
                }
              `;

              const response = await fetch(graphqlEndpoint, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({ query: tenantStatusQuery })
              });

              // Check HTTP status first - 403 means authorization failed
              if (!response.ok) {
                console.error(`HTTP error! status: ${response.status}`);
                setError('Unable to verify tenant status. Please try again.');
                setUserMetadata(null);
                setTenantStatus(null);
                return;
              }

              const result = await response.json();
              console.log('Tenant status response:', result);

              if (result.data?.getTenantStatus) {
                const status = result.data.getTenantStatus;
                setTenantStatus(status);

                // Check if tenant is active
                if (status.status !== 'active') {
                  console.error(`Tenant is not active. Status: ${status.status}`);
                  setError(`Your organization account is ${status.status}. Please contact your administrator for access.`);
                  setUserMetadata(null);
                } else {
                  // SUCCESS - tenant is active, now set userMetadata
                  console.log('Tenant is active, granting access');
                  setUserMetadata(metadata);
                  setError(null);
                }
              } else if (result.errors) {
                console.error('GraphQL errors:', result.errors);
                setError('Unable to verify tenant status. Please try again.');
                setUserMetadata(null);
                setTenantStatus(null);
              } else {
                // No data and no errors - unexpected response
                console.error('Unexpected response format:', result);
                setError('Unable to verify tenant status. Please try again.');
                setUserMetadata(null);
                setTenantStatus(null);
              }
            } catch (statusError) {
              console.error('Error fetching tenant status:', statusError);
              setError('Unable to verify tenant status. Please try again.');
              setUserMetadata(null);
              setTenantStatus(null);
            }
          }
        } catch (e) {
          console.error('Error processing user metadata:', e);
          setError('Failed to process user tenant information: ' + e.message);
          setUserMetadata(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        console.log('User not authenticated, no tenant will be available');
        setIsLoading(false);
        setUserMetadata(null);
        // Don't set an error for unauthenticated users - that's a normal state
      }
    };
    
    fetchMetadata();
  }, [isAuthenticated, user, getAccessTokenSilently]);

  const value = {
    userMetadata,
    tenantStatus,
    isLoading: isLoading || auth0Loading,
    error,
    rawUser: user,
    hasTenant: !!(userMetadata && userMetadata.company)
  };

  // DEBUG: Log context state on every render
  console.log('🔄 UserMetadataContext state:', {
    hasUserMetadata: !!userMetadata,
    userMetadata: userMetadata,
    hasTenantStatus: !!tenantStatus,
    isLoading: value.isLoading,
    hasError: !!error,
    error: error,
    hasTenant: value.hasTenant
  });

  return (
    <UserMetadataContext.Provider value={value}>
      {children}
    </UserMetadataContext.Provider>
  );
}

export function useUserMetadata() {
  const context = useContext(UserMetadataContext);
  if (context === undefined) {
    throw new Error('useUserMetadata must be used within a UserMetadataProvider');
  }
  return context;
}
